// The Plant dock's Alarms tab: every active alarm with its unit, age and ACK, then the most recent alarms that cleared.
// The header banner shows the newest few; this is the whole list, where a visitor can work through them.
// Rows are rebuilt only when the set / order / ack state changes; ages update in place, so a click is never eaten.

const HISTORY_MAX = 8

/** "4 s", "12 min", "1 h 05" from plant seconds. */
export function ageText(s) {
  s = Math.max(0, Math.floor(Number(s) || 0))
  if (s < 60) return `${s} s`
  if (s < 3600) return `${Math.floor(s / 60)} min`
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  return `${h} h ${String(m).padStart(2, "0")}`
}

/**
 * @param root   the tab pane (dock.el("alarms"))
 * @param model  the plant: alarms (Map id → {id, severity, text, unit, raisedAt, acked}), ackAlarm(id), onEvent, t
 * @param onAck  called after an ACK (the host re-renders the banner and outlines)
 */
export function createAlarmsView(root, { model, onAck = () => {}, doc = root.ownerDocument } = {}) {
  const h = (tag, cls, text) => { const e = doc.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e }
  const head = h("p", "alv-head")
  const list = h("ul", "alv-list"); list.setAttribute("aria-label", "Active alarms")
  const empty = h("p", "alv-empty", "No active alarms. Inject a fault from Controls to raise one.")
  const histTitle = h("div", "ctl-label alv-hist-title", "Recently cleared")
  const hist = h("ul", "alv-hist"); hist.setAttribute("aria-label", "Recently cleared alarms")
  root.append(head, list, empty, histTitle, hist)

  const cleared = []   // newest first: { id, text, severity, clearedAt }
  // alarm.cleared carries only the id (the record is already gone), so remember each alarm's words as it is raised.
  const seen = new Map()
  for (const a of model.alarms.values()) seen.set(a.id, { text: a.text, severity: a.severity })
  const off = model.onEvent((e) => {
    if (e.type === "alarm.raised" || e.type === "alarm.updated") {
      const was = seen.get(e.id) || {}
      seen.set(e.id, { text: e.text || was.text || e.id, severity: e.severity || was.severity || "warning" })
      return
    }
    if (e.type !== "alarm.cleared") return
    const w = seen.get(e.id) || { text: e.id, severity: "warning" }
    cleared.unshift({ id: e.id, text: w.text, severity: w.severity, clearedAt: model.t })
    if (cleared.length > HISTORY_MAX) cleared.pop()
  })

  list.addEventListener("click", (e) => {
    const b = e.target.closest ? e.target.closest("[data-ack]") : null
    if (!b) return
    model.ackAlarm(b.dataset.ack)
    render(); onAck(b.dataset.ack)
  })

  let key = null, histKey = null
  function render() {
    const active = Array.from(model.alarms.values()).sort((a, b) => (a.severity === b.severity ? b.raisedAt - a.raisedAt : a.severity === "alarm" ? -1 : 1))
    const unacked = active.filter((a) => !a.acked).length
    head.textContent = active.length ? `${active.length} active · ${unacked} unacknowledged` : ""
    head.hidden = !active.length
    empty.hidden = active.length > 0
    const k = active.map((a) => `${a.id}:${a.severity}:${a.acked ? 1 : 0}`).join("|")
    if (k !== key) {
      key = k
      list.textContent = ""
      for (const a of active) {
        const li = h("li", `alarm-row sev-${a.severity}${a.acked ? "" : " alarm-pulse"}`); li.dataset.alarm = a.id
        li.append(h("span", "alarm-dot"), h("span", "alarm-text", a.text), h("span", "alarm-age"))
        li.firstChild.setAttribute("aria-hidden", "true")
        if (a.acked) li.append(h("span", "alarm-acked", "ACKED"))
        else { const b = h("button", "btn alarm-ack", "ACK"); b.type = "button"; b.dataset.ack = a.id; b.setAttribute("aria-label", `Acknowledge: ${a.text}`); li.append(b) }
        list.append(li)
      }
    }
    for (const li of list.children) {
      const a = model.alarms.get(li.dataset.alarm); if (!a) continue
      const age = li.querySelector(".alarm-age"); if (age) age.textContent = ageText(model.t - a.raisedAt)
    }
    const hk = cleared.map((c) => c.id + c.clearedAt).join("|")
    if (hk !== histKey) {
      histKey = hk
      hist.textContent = ""
      for (const c of cleared) { const li = h("li", `alv-hist-row sev-${c.severity}`, c.text); hist.append(li) }
    }
    histTitle.hidden = hist.hidden = cleared.length === 0
  }
  render()
  return { render, cleared: () => cleared.slice(), destroy() { if (typeof off === "function") off() } }
}
